import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  PipelineResponse,
  FunnelResponse,
  ActivityResponse,
  AttendanceResponse,
  CallCenterResponse,
  ForecastResponse,
} from '../types';

// All /api/v2/analytics/* endpoints are `requireAdmin` on top of `requireAuth`
// (see api-v2/index.js). Each hook accepts an `enabled` flag so the page can
// gate the requests to Administrator/Executive viewers only and avoid a
// guaranteed 403 for the Employee tier.

/**
 * Pipeline dashboard (Requirement 1). When `stage` is set the server also
 * returns a bounded `deals` detail list; `limit`/`offset` are forwarded to
 * page that list (Req 1.5, 15.1).
 */
export function usePipeline(params: {
  stage?: string | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const { stage, limit, offset, enabled = true } = params;
  return useQuery({
    queryKey: ['analytics', 'pipeline', { stage: stage ?? null, limit, offset }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (stage) qs.set('stage', stage);
      if (stage && limit != null) qs.set('limit', String(limit));
      if (stage && offset != null) qs.set('offset', String(offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data } = await api.get<PipelineResponse>(`/api/v2/analytics/pipeline${suffix}`);
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Lead conversion funnel (Requirement 2). Optional `from`/`to` on created_at. */
export function useFunnel(params: { from?: string; to?: string; enabled?: boolean }) {
  const { from, to, enabled = true } = params;
  return useQuery({
    queryKey: ['analytics', 'funnel', { from, to }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data } = await api.get<FunnelResponse>(`/api/v2/analytics/funnel${suffix}`);
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Activity dashboard (Requirement 3). Optional `from`/`to` on created_at. */
export function useActivityAnalytics(params: { from?: string; to?: string; enabled?: boolean }) {
  const { from, to, enabled = true } = params;
  return useQuery({
    queryKey: ['analytics', 'activity', { from, to }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data } = await api.get<ActivityResponse>(`/api/v2/analytics/activity${suffix}`);
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Attendance dashboard (Requirement 4). Optional `log_date` (YYYY-MM-DD). */
export function useAttendance(params: { logDate?: string; enabled?: boolean }) {
  const { logDate, enabled = true } = params;
  return useQuery({
    queryKey: ['analytics', 'attendance', { logDate }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (logDate) qs.set('log_date', logDate);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data } = await api.get<AttendanceResponse>(`/api/v2/analytics/attendance${suffix}`);
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Call-center productivity dashboard (Requirement 5). */
export function useCallCenter(params: { enabled?: boolean } = {}) {
  const { enabled = true } = params;
  return useQuery({
    queryKey: ['analytics', 'call-center'],
    queryFn: async () => {
      const { data } = await api.get<CallCenterResponse>('/api/v2/analytics/call-center');
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Revenue forecast (Requirement 6). When `aiWeighted` is set the request adds
 * `?factor=ai`, which recomputes the probability factor from `ai.js`
 * `dealWinProbability`; the server then echoes `aiWeighted: true` so the UI can
 * label the whole forecast an AI_Suggestion (Req 6.8, 14.3). By default the
 * server uses the authoritative rep-entered `Deal.probability` (Req 6.3).
 */
export function useForecast(params: { aiWeighted?: boolean; enabled?: boolean } = {}) {
  const { aiWeighted = false, enabled = true } = params;
  return useQuery({
    queryKey: ['analytics', 'forecast', { aiWeighted }],
    queryFn: async () => {
      const suffix = aiWeighted ? '?factor=ai' : '';
      const { data } = await api.get<ForecastResponse>(`/api/v2/analytics/forecast${suffix}`);
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}
